import {
  type FC, useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { createUuid } from '~/_shared/utils/createUuid';
import { useGetGroupInfoBySpreadsheetRowId } from '~/_shared/utils/grouping/useGetGroupInfoBySpreadsheetRowId.hook';
import { useGetSpreadsheetRowIdsByGroupInfo } from '~/_shared/utils/grouping/useGetSpreadsheetRowIdsByGroupInfo.hook';
import { useIsMapInteractionActive } from '~/_shared/utils/hooks/useIsMapInteractionActive';
import { areSpreadsheetRowIdsEqual } from '~/_shared/utils/spreadsheet/generalSpreadsheet.helpers';
import { useAddressInfoByRowIds } from '~/_shared/utils/spreadsheet/useAddressInfoByRowIds.hook';
import { useToolLocationCallbacks } from '~/customizeLocationPanel/roundButtonMenu/contextMenuStructure/useToolLocationCallbacks';
import { useMap } from '~/map/map/mapContext';
import { useProjectionOverlay } from '~/map/map/useProjectionOverlay';
import { hideMarkerContextMenu } from '~/store/frontendState/markersContextMenu/markersContextMenu.actionCreators';
import { useMarkersContextMenuActiveSelector } from '~/store/frontendState/markersContextMenu/markersContextMenu.selectors';
import { moveMarkerLabelsOpenModal } from '~/store/frontendState/moveMarkerLabels/moveMarkerLabels.actionCreators';
import { moveMarkersOpenModal } from '~/store/frontendState/moveMarkers/moveMarkers.actionCreators';
import { checkIsSalesforceMap } from '~/store/mapInfo/mapInfo.helpers';
import {
  useIsConnectedLayeredMapSelector, useMapInfoDataSelector,
} from '~/store/mapInfo/mapInfo.selectors';
import { useLabelsInsideMarkersActiveSelector } from '~/store/mapSettings/makersGeneral/mapSettingsMarkersGeneral.selectors';
import {
  mapSettingsMarkersAddPriorityMarkers, mapSettingsMarkersRemoveMarkersFromPriority, mapSettingsMarkersResetPriorityMarkers,
} from '~/store/mapSettings/markers/mapSettingsMarkers.actionCreators';
import { useMapSettingsMarkersPriorityMarkersSelector } from '~/store/mapSettings/markers/mapSettingsMarkers.selectors';
import { useIsMapEditDisabled } from '~/store/selectors/useMapInfoSelectors';
import { MapMarkerContextMenuComponent } from './mapMarkerContextMenu.component';

export const MapMarkerContextMenuContainer: FC = () => {
  const map = useMap();
  const dispatch = useDispatch();
  const isEditModeDisabled = useIsMapEditDisabled();
  const isLayeredMapConnected = useIsConnectedLayeredMapSelector();
  const labelsInsideMarkersActive = useLabelsInsideMarkersActiveSelector();
  const markerContextMenu = useMarkersContextMenuActiveSelector();
  const mapInteractionActive = useIsMapInteractionActive();
  const { fromLatLngToContainerPixel } = useProjectionOverlay(map);
  const priorityMarkers = useMapSettingsMarkersPriorityMarkersSelector();
  const mapInfo = useMapInfoDataSelector();

  const firstMarkerRowId = markerContextMenu?.rowIds[0];

  const firstMarkerGroupInfo = useGetGroupInfoBySpreadsheetRowId(firstMarkerRowId);
  const allIndividualMarkersInGroup = useGetSpreadsheetRowIdsByGroupInfo(firstMarkerGroupInfo);

  const allMarkeridsInTheSameGroup = useMemo(() => {
    if (!firstMarkerGroupInfo || !markerContextMenu?.rowIds) {
      return false;
    }

    return markerContextMenu.rowIds.every(markerId =>
      allIndividualMarkersInGroup.some(rowId => areSpreadsheetRowIdsEqual(rowId, markerId))
    );
  }, [allIndividualMarkersInGroup, firstMarkerGroupInfo, markerContextMenu?.rowIds]);

  const actionsAllowed = !mapInteractionActive && !isEditModeDisabled;

  const isMarkerInPriorityMarkers = useMemo(() => {
    if (!markerContextMenu?.rowIds || !priorityMarkers.length) {
      return false;
    }
    return markerContextMenu.rowIds.some(markerId =>
      priorityMarkers.some(priorityMarkerId =>
        priorityMarkerId.spreadsheetId === markerId.spreadsheetId &&
        priorityMarkerId.rowId === markerId.rowId
      )
    );
  }, [markerContextMenu?.rowIds, priorityMarkers]);

  const onMoveMarkers = useCallback(() => {
    dispatch(moveMarkersOpenModal());
  }, [dispatch]);

  const onMoveMarkerLabels = useCallback(() => {
    dispatch(moveMarkerLabelsOpenModal());
  }, [dispatch]);

  const onBringToFront = useCallback(() => {
    if (markerContextMenu?.rowIds) {
      dispatch(mapSettingsMarkersAddPriorityMarkers(
        markerContextMenu.rowIds
      ));
    }
  }, [dispatch, markerContextMenu?.rowIds]);

  const onResetAllBringToFront = useCallback(() => {
    dispatch(mapSettingsMarkersResetPriorityMarkers());
  }, [dispatch]);

  const onRemoveFromBringToFront = useCallback(() => {
    if (markerContextMenu?.rowIds) {
      dispatch(mapSettingsMarkersRemoveMarkersFromPriority(
        markerContextMenu.rowIds
      ));
    }
  }, [dispatch, markerContextMenu?.rowIds]);

  const onBringGroupToFront = useCallback(() => {
    dispatch(mapSettingsMarkersAddPriorityMarkers(
      allIndividualMarkersInGroup
    ));
  }, [allIndividualMarkersInGroup, dispatch]);

  const oneMarkerIdForAddressData = markerContextMenu?.rowIds[0];

  const addressInfoByRowIdsParams = useMemo(() => ({
    spreadsheetRowIds: oneMarkerIdForAddressData ? [oneMarkerIdForAddressData] : [],
  }), [oneMarkerIdForAddressData]);

  const addressInfoByRowId = useAddressInfoByRowIds(addressInfoByRowIdsParams);

  const locationAddress = oneMarkerIdForAddressData
    ? addressInfoByRowId.get(oneMarkerIdForAddressData.spreadsheetId)?.get(oneMarkerIdForAddressData.rowId)?.addressFallbackToLatLng
    : null;

  const toolLocationCallbacks = useToolLocationCallbacks(
    oneMarkerIdForAddressData,
    locationAddress,
  );

  const isSalesforceMap = checkIsSalesforceMap(mapInfo);
  const allowMoveMarkers = actionsAllowed && !isLayeredMapConnected && !isSalesforceMap;
  const allowMoveMarkerLabels = actionsAllowed && labelsInsideMarkersActive;
  const allowBringToFront = actionsAllowed && markerContextMenu?.rowIds;
  const allowRemoveBringToFront = actionsAllowed && isMarkerInPriorityMarkers && markerContextMenu?.rowIds;
  const allowResetBringToFront = actionsAllowed && priorityMarkers.length > 0;
  const allowBringGroupToFront = actionsAllowed && allMarkeridsInTheSameGroup;

  const isMenuEmpty = !onMoveMarkers && !onMoveMarkerLabels && !onBringToFront
    && !allowBringGroupToFront && !allowResetBringToFront && !allowRemoveBringToFront;

  const menuPosition = useMemo(() => {
    const positionKey = createUuid();

    if (!markerContextMenu) {
      return null;
    }

    const latToPx = fromLatLngToContainerPixel(markerContextMenu.position.latLng);

    return latToPx ? {
      key: positionKey,
      x: latToPx.x + markerContextMenu.position.offset.x,
      y: latToPx.y + markerContextMenu.position.offset.y,
    } : null;
  }, [fromLatLngToContainerPixel, markerContextMenu]);

  const onHide = useCallback(() => dispatch(hideMarkerContextMenu()), [dispatch]);

  if (isMenuEmpty || !markerContextMenu || !menuPosition) {
    return null;
  }

  return (
    <MapMarkerContextMenuComponent
      key={menuPosition.key} // reload component when menu position changes
      map={map}
      menuPosition={menuPosition}
      onHide={onHide}
      onBringToFront={allowBringToFront ? onBringToFront : undefined}
      onBringGroupToFront={allowBringGroupToFront ? onBringGroupToFront : undefined}
      onResetAllBringToFront={allowResetBringToFront ? onResetAllBringToFront : undefined}
      onRemoveFromBringToFront={allowRemoveBringToFront ? onRemoveFromBringToFront : undefined}
      onMoveMarkers={allowMoveMarkers ? onMoveMarkers : undefined}
      onMoveMarkerLabels={allowMoveMarkerLabels ? onMoveMarkerLabels : undefined}
      onAddRadius={toolLocationCallbacks.drawRadiusCallback}
      onAddDtp={toolLocationCallbacks.addDTPCallback}
    />
  );
};
